<script setup lang="ts">
import { useTeamStore } from "@/stores/teams.ts";
import { type GameTeam, useGameStore } from "@/stores/games.ts";
import { ref, watch } from "vue";
import TeamDisplay from "@/components/TeamDisplay.vue";
import { useGroupStore } from "@/stores/groups.ts";

const teamStore = useTeamStore();
const gameStore = useGameStore();
const groupStore = useGroupStore();

const modelValue = defineModel<GameTeam>({
  type: Object,
  default: () => ({
    id: "",
    game: null,
    placement: null
  })
});

const emit = defineEmits<{
  (e: "inputChange", callback: (value: GameTeam) => void): void
}>();

const selectedTeamId = ref<string>("");
const selectedGameId = ref<string>("");
const selectedPlacementGroup = ref<string>("");
const selectedPlacement = ref<number>(0);
const errorMessage = ref<string | null>(null);

function onInputUpdate() {
  if (selectedTeamId.value === "GAME_WIN_SELECT" || selectedTeamId.value === "GAME_LOSS_SELECT") {
    if (!selectedGameId.value) {
      errorMessage.value = "Bitte wähle ein Spiel aus";
      return;
    }
    modelValue.value.id = null;
    modelValue.value.game = {
      id: selectedGameId.value,
      win: selectedTeamId.value === "GAME_WIN_SELECT"
    }
    modelValue.value.placement = null;
    errorMessage.value = null;
    emit("inputChange", JSON.parse(JSON.stringify(modelValue.value)));
    return;
  }

  if (selectedTeamId.value === "PLACEMENT_SELECT") {
    if (!selectedPlacementGroup.value || selectedPlacement.value === null) {
      errorMessage.value = "Bitte wähle eine Gruppe und Platzierung aus";
      return;
    }
    modelValue.value.id = null;
    modelValue.value.game = null;
    modelValue.value.placement = {
      id: selectedPlacementGroup.value,
      place: selectedPlacement.value
    };
    errorMessage.value = null;
    emit("inputChange", JSON.parse(JSON.stringify(modelValue.value)));
    return;
  }

  if (!selectedTeamId.value) {
    errorMessage.value = "Bitte wähle ein Team aus";
    return;
  }

  modelValue.value.id = selectedTeamId.value;
  modelValue.value.game = null;
  modelValue.value.placement = null;
  errorMessage.value = null;
  selectedGameId.value = "";
  selectedPlacementGroup.value = "";
  selectedPlacement.value = 0;
  emit("inputChange", JSON.parse(JSON.stringify(modelValue.value)));
}
function onModelUpdate() {
  if (modelValue.value.game) {
    selectedTeamId.value = modelValue.value.game.win ? "GAME_WIN_SELECT" : "GAME_LOSS_SELECT";
    selectedGameId.value = modelValue.value.game.id;
  } else if (modelValue.value.placement) {
    selectedTeamId.value = "PLACEMENT_SELECT";
    selectedPlacementGroup.value = modelValue.value.placement.id;
    selectedPlacement.value = modelValue.value.placement.place;
  } else if (modelValue.value.id) {
    selectedTeamId.value = modelValue.value.id;
    selectedGameId.value = "";
    selectedPlacementGroup.value = "";
    selectedPlacement.value = 0;
  } else {
    selectedTeamId.value = "";
    selectedGameId.value = "";
    selectedPlacementGroup.value = "";
    selectedPlacement.value = 0;
  }
}

watch(() => modelValue, onModelUpdate, { immediate: true });
</script>

<template>
  <div class="team-select-container">
    <div class="preview">
      <TeamDisplay
        v-if="modelValue.id"
        :team="teamStore.getTeamById(modelValue.id)"
        hide_name
      />
      <div v-else class="logo-replacement">
        {{
          modelValue.game ? (modelValue.game.win ? 'W' : 'L') : (modelValue.placement ? modelValue.placement.place : '?')
        }}
      </div>
    </div>
    <div class="input-area">
      <select v-model="selectedTeamId" class="team-select" @change="onInputUpdate">
        <option v-for="team in teamStore.teams" :key="team.id" :value="team.id">
          {{ team.name }}
        </option>
        <option value="GAME_WIN_SELECT">
          Gewinner von Spiel
        </option>
        <option value="GAME_LOSS_SELECT">
          Verlierer von Spiel
        </option>
        <option value="PLACEMENT_SELECT">
          Platzierung von Gruppe
        </option>
        <option value="" disabled>
          -- Team auswählen --
        </option>
      </select>
      <select v-model="selectedGameId" class="game-select" v-if="selectedTeamId === 'GAME_WIN_SELECT' || selectedTeamId === 'GAME_LOSS_SELECT'" @change="onInputUpdate">
        <option v-for="(game, index) in gameStore.games" :key="game.id" :value="game.id">
          Spiel {{ index + 1 }}: {{ game.dateTime }}
        </option>
        <option value="" disabled>
          -- Spiel auswählen --
        </option>
      </select>
      <select v-model="selectedPlacementGroup" class="placement-select" v-if="selectedTeamId === 'PLACEMENT_SELECT'" @change="onInputUpdate">
        <option v-for="group in groupStore.groups" :key="group.id" :value="group.id">
          {{ group.name }}
        </option>
        <option value="" disabled>
          -- Gruppe auswählen --
        </option>
      </select>
      <select v-model="selectedPlacement" class="placement-number-select" v-if="selectedTeamId === 'PLACEMENT_SELECT' && selectedPlacementGroup !== ''" @change="onInputUpdate">
        <option
          v-for="n in groupStore.groups.find(group => group.id === selectedPlacementGroup)?.placements.length || 0"
          :key="n"
          :value="n"
        >
          Platz {{ n }}
        </option>
        <option :value="0" disabled>
          -- Platzierung auswählen --
        </option>
      </select>
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
    </div>
  </div>
</template>

<style scoped>
.team-select-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.logo-replacement {
  width: 2rem;
  height: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--main-color-dark);
  color: white;
  font-weight: bold;
  border-radius: 50%;
}

.input-area {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  background: var(--color-layer-2);
  border-radius: .5rem;
  border: .125rem solid var(--color-layer-1);
  overflow: hidden;
  transition: border-color 0.2s ease-in-out;

  &:hover,
  &:focus-within {
    border-color: var(--main-color);
  }

  input,
  select {
    padding: .5rem;
    border: none;
    background: transparent;
    color: var(--text-color-layer-2);
    font-size: 1rem;
    outline: none;
    cursor: pointer;
    transition: background 0.2s ease-in-out;

    &:focus,
    &:hover {
      background: var(--color-layer-0);
    }
  }

  .error-message {
    pointer-events: none;
    user-select: none;
    color: var(--danger-color);
    opacity: 0.8;
    font-size: 0.875rem;
    text-align: center;
  }
}
</style>
